// File: DashboardKpiOverview.tsx
// KPI overview: each KPI vs its goal from Settings, shown as a goal bar (tick = goal) + status.
import React from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import { daysInMonthForLabel, fmtInt, fmtMoney, getRollingDashboardLabelForDate, getRollingDashboardLabels } from './DashboardHelpers';
import { DashboardCard } from './DashboardLayout';
import type { DashboardPurchasingRow, DashboardReturnsRow, DashboardSalesRow } from 'src/pages/dashboard/DashboardProviders';
import type { Period } from 'src/pages/dashboard/DashboardFilters';

/* ----------------------------------------------------------------------------
 * KPI model
 * ---------------------------------------------------------------------------*/

/** atLeast = higher is good, atMost = lower is good, near = should stay close to the goal (budget pacing) */
export type DashboardKpiRule = 'atLeast' | 'atMost' | 'near';

export type DashboardKpi = {
  id: string;
  title: string;
  unitLabel: string;
  icon: 'purchasing' | 'orders' | 'money' | 'returns';
  value: number;
  target: number;
  display: string;
  targetDisplay: string;
  rule: DashboardKpiRule;
  tooltip?: string;
};

type KpiSetting = { enabled?: string | boolean; threshold?: string | number };
type BuildArgs = {
  period: Period;
  dashboardSales: DashboardSalesRow[];
  dashboardReturns: DashboardReturnsRow[];
  dashboardPurchasing: DashboardPurchasingRow[];
  settingsKpi: Record<string, KpiSetting | undefined>;
  t: (key: string, options?: any) => string;
  now?: Date;
};

const parseEnabledAndThreshold = (entry?: KpiSetting) => ({
  enabled: String(entry?.enabled ?? 'false').toLowerCase() === 'true',
  threshold: Number(entry?.threshold ?? 0) || 0,
});

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));
const isoWeekKey = (date: Date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((+d - +yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${pad2(week)}`;
};
const fmtDec = (n: number, digits = 2) => n.toLocaleString('sv-SE', { maximumFractionDigits: digits });

/** Days in the rolling window (Week 7, Month 4×7 = 28, Year = days in the 12 month labels). */
export const windowDays = (period: Period, labels: string[]) => {
  if (period === 'Week') return 7;
  if (period === 'Month') return 28;
  return Math.max(1, labels.map(daysInMonthForLabel).reduce((a, b) => a + b, 0));
};

/**
 * Same KPIs and settings as before (TotalDailyOrders, OrderValueAvrage, ReturnsWeekly, PurchasesVsBudgetYTD),
 * now returning raw value + goal so they can be drawn as goal bars.
 * Uses the same rolling window as the rest of the dashboard (DashboardHelpers).
 */
export const buildDashboardKpis = ({ period, dashboardSales, dashboardReturns, dashboardPurchasing, settingsKpi, t, now = new Date() }: BuildArgs): DashboardKpi[] => {
  const kpis: DashboardKpi[] = [];
  const labels = getRollingDashboardLabels(period, now);
  const salesInWindow = (dashboardSales ?? []).filter(row => Boolean(getRollingDashboardLabelForDate(row.SalesDate, period, labels)));
  const divisorDays = windowDays(period, labels);

  const totalOrders = salesInWindow.reduce((s, row) => s + (Number(row.Count ?? 0) || 0), 0);
  const sumNet = salesInWindow.reduce((s, row) => s + (Number(row.TotalAmountNet ?? 0) || 0), 0);
  const avgDailyOrders = divisorDays > 0 ? totalOrders / divisorDays : 0;
  const avgOrderValue = totalOrders > 0 ? sumNet / totalOrders : 0;

  // Purchasing pace vs yearly budget (shown whenever a yearly goal is set — same as before)
  {
    const yearlyGoal = Number(settingsKpi?.PurchasesVsBudgetYTD?.threshold ?? 0) || 0;
    if (yearlyGoal > 0) {
      const year = now.getFullYear();
      const startOfYear = new Date(year, 0, 1);
      const dayOfYear = Math.floor((+new Date(year, now.getMonth(), now.getDate()) - +startOfYear) / 86400000) + 1;
      const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
      const ytd = (dashboardPurchasing ?? []).reduce((acc, po) => {
        const created = po?.PurchaseOrderCreated ? new Date(po.PurchaseOrderCreated) : null;
        if (!created || created.getFullYear() !== year || created > now) return acc;
        return acc + (Number(po?.OrderValueInDefaultCurreny ?? 0) || 0);
      }, 0);
      const dailyBudget = yearlyGoal / (isLeap ? 366 : 365);
      const dailyAvg = ytd / (dayOfYear || 1);
      if (Number.isFinite(dailyAvg) && Number.isFinite(dailyBudget)) {
        kpis.push({
          id: 'PurchasesDailyAverage', title: t('Purchasing (avg/day)'), unitLabel: t('YTD, per day'), icon: 'purchasing',
          value: dailyAvg, target: dailyBudget, rule: 'near',
          display: `${fmtInt(dailyAvg)} ${t('SEK/day')}`, targetDisplay: `${fmtInt(dailyBudget)} ${t('SEK/day')}`,
          tooltip: t('Average purchasing per day this year compared with the yearly purchasing budget spread evenly over the year.'),
        });
      }
    }
  }

  {
    const { enabled, threshold } = parseEnabledAndThreshold(settingsKpi?.TotalDailyOrders);
    if (enabled) kpis.push({
      id: 'TotalDailyOrders', title: t('Total Daily Orders'), unitLabel: t('Avg/Day'), icon: 'orders',
      value: avgDailyOrders, target: threshold, rule: 'atLeast',
      display: `${fmtDec(avgDailyOrders)} ${t('st')}`, targetDisplay: `${fmtDec(threshold)} ${t('st')}`,
    });
  }

  {
    const { enabled, threshold } = parseEnabledAndThreshold(settingsKpi?.OrderValueAvrage);
    if (enabled) kpis.push({
      id: 'OrderValueAvrage', title: t('Order Value Avg'), unitLabel: t('Kr/Order'), icon: 'money',
      value: avgOrderValue, target: threshold, rule: 'atLeast',
      display: fmtMoney(avgOrderValue), targetDisplay: fmtMoney(threshold),
    });
  }

  {
    const { enabled, threshold } = parseEnabledAndThreshold(settingsKpi?.ReturnsWeekly);
    if (enabled) {
      // Same rule as before: the latest ISO week that has sales in the window
      const weeks = salesInWindow.map(row => isoWeekKey(new Date(row.SalesDate))).sort();
      const targetWeek = weeks.length ? weeks[weeks.length - 1] : isoWeekKey(now);
      const returnsCount = (dashboardReturns ?? [])
        .filter(row => isoWeekKey(new Date(row.ReturnCreateDate)) === targetWeek)
        .reduce((s, row) => s + (Number(row.Count ?? 0) || 0), 0);
      kpis.push({
        id: 'ReturnsWeekly', title: t('Returns (weekly)'), unitLabel: t('Qty/Week'), icon: 'returns',
        value: returnsCount, target: threshold, rule: 'atMost',
        display: `${fmtInt(returnsCount)} ${t('st')}`, targetDisplay: `${fmtInt(threshold)} ${t('st')}`,
      });
    }
  }

  return kpis;
};

/* ----------------------------------------------------------------------------
 * Presentation
 * ---------------------------------------------------------------------------*/

type Status = { tone: 'good' | 'warn' | 'bad'; label: string };

const kpiStatus = (k: DashboardKpi, t: (key: string) => string): Status => {
  const ratio = k.target > 0 ? k.value / k.target : 0;
  if (k.rule === 'near') {
    const d = Math.abs(ratio - 1) * 100;
    if (k.target <= 0) return { tone: 'warn', label: t('No goal') };
    if (d <= 5) return { tone: 'good', label: t('On budget') };
    if (d <= 10) return { tone: 'warn', label: t('Slightly off') };
    return { tone: 'bad', label: ratio > 1 ? t('Over budget') : t('Under budget') };
  }
  if (k.rule === 'atMost') return k.value <= k.target ? { tone: 'good', label: t('Within limit') } : { tone: 'bad', label: t('Above limit') };
  return k.value >= k.target ? { tone: 'good', label: t('On target') } : { tone: 'bad', label: t('Below target') };
};

const KpiIcon = ({ kind }: { kind: DashboardKpi['icon'] }) => {
  const sx = { fontSize: 17, color: 'text.secondary' };
  if (kind === 'purchasing') return <ShoppingCartRoundedIcon sx={sx} />;
  if (kind === 'orders') return <LocalMallRoundedIcon sx={sx} />;
  if (kind === 'money') return <PaidRoundedIcon sx={sx} />;
  return <KeyboardReturnRoundedIcon sx={sx} />;
};

export const DashboardKpiRow = ({ kpi }: { kpi: DashboardKpi }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const status = kpiStatus(kpi, t);
  const ratio = kpi.target > 0 ? kpi.value / kpi.target : 0;
  const deltaPct = (ratio - 1) * 100;
  const toneColor = status.tone === 'good' ? theme.palette.success.main : status.tone === 'warn' ? theme.palette.warning.main : theme.palette.error.main;

  return (
    <Box className="kpi-row" sx={{ py: 1.25, borderBottom: 1, borderColor: 'divider' }} title={kpi.tooltip}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600, color: 'text.primary' }}>
          <KpiIcon kind={kpi.icon} />{kpi.title}
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.875, py: 0.125, borderRadius: 999, fontSize: 11.5, fontWeight: 650, color: toneColor, bgcolor: alpha(toneColor, 0.14) }}>
          {status.tone === 'good' ? <CheckCircleRoundedIcon sx={{ fontSize: 13 }} /> : <WarningRoundedIcon sx={{ fontSize: 13 }} />}
          {status.label}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', my: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          <Typography component="span" sx={{ fontSize: 17, fontWeight: 700, color: 'text.primary', mr: 0.75 }}>{kpi.display}</Typography>
          {kpi.unitLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('Goal')} {kpi.targetDisplay} ·{' '}
          <Typography component="span" variant="caption" sx={{ fontWeight: 650, color: 'text.primary' }}>{`${deltaPct >= 0 ? '+' : ''}${deltaPct.toFixed(0)}%`}</Typography>
        </Typography>
      </Box>
      <Tooltip title={t('The tick marks the goal')} placement="top" arrow>
        <Box sx={{ position: 'relative', height: 8, mt: 0.75, borderRadius: 4, bgcolor: 'action.hover' }} role="img" aria-label={`${Math.round(ratio * 100)}%`}>
          <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 4, width: `${Math.min(ratio / 2, 1) * 100}%`, bgcolor: toneColor }} />
          <Box sx={{ position: 'absolute', top: -4, bottom: -4, left: '50%', width: 2, borderRadius: 1, bgcolor: 'text.primary' }} />
        </Box>
      </Tooltip>
    </Box>
  );
};

export const DashboardKpiOverviewCard = ({ kpis }: { kpis: DashboardKpi[] }) => {
  const { t } = useTranslation();
  return (
    <DashboardCard area="kpi" title={t('KPI Overview')} bodySx={{ justifyContent: 'space-around' }}>
      {kpis.length ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              columnGap: 3.5,
              '& .kpi-row:last-of-type': { borderBottom: 0 },
              '@container (min-width: 600px)': {
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                '& .kpi-row:nth-last-of-type(2):nth-of-type(odd)': { borderBottom: 0 },
              },
            }}
          >
            {kpis.map(k => <DashboardKpiRow key={k.id} kpi={k} />)}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', pt: 1 }}>
            {t('Goals are set in')} <Link href="/settings/webclient">{t('Settings')}</Link>
          </Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('No enabled KPIs.')} <Link href="/settings/webclient">{t('Set and Enable in settings')}</Link>
        </Typography>
      )}
    </DashboardCard>
  );
};
